///
/// EarthBrowser
///
/// Created by Matt Giger on December 27, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { createSlice, type PayloadAction } from "@reduxjs/toolkit"

export interface GitRemoteConfig {
	url: string
	name: string // e.g., "origin"
}

export interface GitCredentials {
	username: string
	token: string // Personal access token
}

export interface GitConfigState {
	// Remote repository configuration
	remotes: Record<string, GitRemoteConfig> // keyed by remote name

	// Credentials (stored encrypted in localStorage) - kept for backward compatibility
	credentials: Record<string, GitCredentials> // keyed by remote name

	// GitHub OAuth connection status (preferred auth method)
	githubConnected: boolean

	// Default remote for push/pull operations
	defaultRemote: string | null

	// Auto-sync settings
	autoFetch: boolean
	autoFetchInterval: number // minutes

	// Last sync timestamps
	lastFetch: Record<string, number> // keyed by remote name (timestamp)
	lastPush: Record<string, number> // keyed by remote name (timestamp)
}

// Default repo URL for initial setup
const DEFAULT_REPO_URL = 'https://github.com/GeodataLabs/eb_scripts.git'

const initialState: GitConfigState = {
	remotes: {
		origin: {
			name: 'origin',
			url: DEFAULT_REPO_URL
		}
	},
	credentials: {},
	githubConnected: false,
	defaultRemote: 'origin',
	autoFetch: false,
	autoFetchInterval: 15, // 15 minutes
	lastFetch: {},
	lastPush: {}
}

export const gitConfigSlice = createSlice({
	name: "gitConfig",
	initialState,
	reducers: {
		// Remote management
		addRemote: (state, action: PayloadAction<{ name: string; url: string }>) => {
			const { name, url } = action.payload
			state.remotes[name] = { name, url }
			if (!state.defaultRemote) {
				state.defaultRemote = name
			}
		},

		removeRemote: (state, action: PayloadAction<string>) => {
			const name = action.payload
			delete state.remotes[name]
			delete state.credentials[name]
			delete state.lastFetch[name]
			delete state.lastPush[name]
			if (state.defaultRemote === name) {
				state.defaultRemote = Object.keys(state.remotes)[0] || null
			}
		},

		updateRemote: (state, action: PayloadAction<{ name: string; url: string }>) => {
			const { name, url } = action.payload
			if (state.remotes[name]) {
				state.remotes[name].url = url
			}
		},

		setDefaultRemote: (state, action: PayloadAction<string>) => {
			if (state.remotes[action.payload]) {
				state.defaultRemote = action.payload
			}
		},

		// Credentials management (legacy - kept for backward compatibility)
		setCredentials: (state, action: PayloadAction<{ remote: string; username: string; token: string }>) => {
			const { remote, username, token } = action.payload
			state.credentials[remote] = { username, token }
		},

		removeCredentials: (state, action: PayloadAction<string>) => {
			delete state.credentials[action.payload]
		},

		// GitHub OAuth connection status
		setGitHubConnected: (state, action: PayloadAction<boolean>) => {
			state.githubConnected = action.payload
		},

		// Auto-sync settings
		setAutoFetch: (state, action: PayloadAction<boolean>) => {
			state.autoFetch = action.payload
		},

		setAutoFetchInterval: (state, action: PayloadAction<number>) => {
			state.autoFetchInterval = action.payload
		},

		// Sync timestamps
		updateLastFetch: (state, action: PayloadAction<{ remote: string; timestamp: number }>) => {
			const { remote, timestamp } = action.payload
			state.lastFetch[remote] = timestamp
		},

		updateLastPush: (state, action: PayloadAction<{ remote: string; timestamp: number }>) => {
			const { remote, timestamp } = action.payload
			state.lastPush[remote] = timestamp
		}
	}
})

export const {
	addRemote,
	removeRemote,
	updateRemote,
	setDefaultRemote,
	setCredentials,
	removeCredentials,
	setGitHubConnected,
	setAutoFetch,
	setAutoFetchInterval,
	updateLastFetch,
	updateLastPush
} = gitConfigSlice.actions

export default gitConfigSlice.reducer

